import { useEffect, useRef, useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const GOOGLE_MAPS_SCRIPT_ID = "google-maps-places-script";
const GOOGLE_MAPS_CALLBACK = "__cashflow4uGooglePlacesReady";
let googlePlacesScriptPromise;

const loadGooglePlaces = (apiKey) => {
  if (window.google?.maps?.places) {
    return Promise.resolve(window.google);
  }

  if (googlePlacesScriptPromise) {
    return googlePlacesScriptPromise;
  }

  googlePlacesScriptPromise = new Promise((resolve, reject) => {
    const existingScript =
      document.getElementById(GOOGLE_MAPS_SCRIPT_ID) ||
      document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');

    const resolveWhenReady = () => {
      if (window.google?.maps?.places) {
        resolve(window.google);
        return;
      }

      reject(new Error("Google Places API did not load."));
    };

    if (existingScript) {
      if (window.google) {
        resolveWhenReady();
        return;
      }

      existingScript.addEventListener("load", resolveWhenReady, { once: true });
      existingScript.addEventListener(
        "error",
        () => reject(new Error("Google Maps script failed to load.")),
        { once: true }
      );
      return;
    }

    window[GOOGLE_MAPS_CALLBACK] = resolveWhenReady;

    const script = document.createElement("script");
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src =
      "https://maps.googleapis.com/maps/api/js" +
      `?key=${encodeURIComponent(apiKey)}` +
      "&libraries=places" +
      `&callback=${GOOGLE_MAPS_CALLBACK}`;
    script.onerror = () => reject(new Error("Google Maps script failed to load."));

    document.head.appendChild(script);
  });

  return googlePlacesScriptPromise;
};

/**
 * Reusable Address Autocomplete Component
 * 
 * Provides Google Places address suggestions with:
 * - US-only address restrictions
 * - Structured address component parsing
 * - Callback with full address details including lat/lng
 * - Standard controlled input interface (value/onChange)
 */
const AddressAutocomplete = ({
  label = "Address",
  value = "",
  onChange,
  onPlaceSelected,
  placeholder = "Enter address",
  className = "",
  id,
  required = false,
  ...inputProps
}) => {
  const inputRef = useRef(null);
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [placesStatus, setPlacesStatus] = useState(apiKey ? "loading" : "unavailable");

  // Stable ref so the place_changed listener always calls the latest prop callbacks
  // without needing to re-attach the listener on every render.
  const onPlaceSelectedRef = useRef(onPlaceSelected);
  const onChangeRef = useRef(onChange);
  useEffect(() => { onPlaceSelectedRef.current = onPlaceSelected; }, [onPlaceSelected]);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  /**
   * Parse address components from Google Places result.
   *
   * Bugs fixed vs. original:
   * 1. Scans all types[] entries per component, not just types[0] — some results
   *    list the relevant type at index > 0.
   * 2. Falls back through city-equivalent types in priority order:
   *    locality → sublocality_level_1 → postal_town → neighborhood
   *    (Google omits `locality` for NYC boroughs, some rural/international addresses.)
   * 3. Uses long_name for city (short_name can be abbreviated in some locales).
   * 4. Returns null fields rather than empty strings when a component is genuinely
   *    absent so callers can distinguish "not returned" from "empty string".
   *
   * Data-dependent limitation (not a code bug):
   *   Some Google Places results for street-level or rural addresses do not include
   *   a `postal_code` component at all. In those cases zip_code will be null and
   *   the Zip Code field is left empty so the user can fill it in manually.
   */
  const parseAddressComponents = (place) => {
    if (!place?.address_components?.length) return null;

    // Build a lookup: type → { long_name, short_name }
    const byType = {};
    place.address_components.forEach((component) => {
      component.types.forEach((type) => {
        if (!byType[type]) byType[type] = component;
      });
    });

    const get = (type, nameKey = "long_name") => byType[type]?.[nameKey] ?? null;

    // City: try each fallback in order
    const city =
      get("locality") ??
      get("sublocality_level_1") ??
      get("postal_town") ??
      get("neighborhood") ??
      null;

    return {
      street: [
        get("street_number"),
        get("route"),
      ].filter(Boolean).join(" ") || "",
      city: city ?? "",
      state: get("administrative_area_level_1") ?? "",
      zip_code: get("postal_code") ?? "",
      country: get("country", "short_name") ?? "US",
      lat: place.geometry?.location?.lat() ?? null,
      lng: place.geometry?.location?.lng() ?? null,
      formatted_address: place.formatted_address ?? "",
    };
  };

  const inputClassName =
    "w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";

  useEffect(() => {
    if (!apiKey || !inputRef.current) {
      setPlacesStatus("unavailable");
      return undefined;
    }

    let autocomplete;
    let placeChangedListener;
    let isMounted = true;

    setPlacesStatus("loading");

    loadGooglePlaces(apiKey)
      .then((google) => {
        if (!isMounted || !inputRef.current) return;

        autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
          types: ["address"],
          componentRestrictions: { country: "us" },
          fields: ["address_components", "geometry", "place_id", "formatted_address"],
        });

        // Use refs so this listener always calls the current prop callbacks
        // even after parent re-renders — fixes the stale closure bug.
        placeChangedListener = autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          if (!place) return;
          const addressData = parseAddressComponents(place);
          if (addressData && onPlaceSelectedRef.current) {
            onPlaceSelectedRef.current(addressData);
          } else if (onChangeRef.current) {
            onChangeRef.current({ target: { value: place.formatted_address ?? "" } });
          }
        });

        setPlacesStatus("ready");
      })
      .catch((error) => {
        console.error("Address autocomplete unavailable:", error);
        if (isMounted) setPlacesStatus("unavailable");
      });

    return () => {
      isMounted = false;
      if (placeChangedListener) placeChangedListener.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);

  return (
    <div className={className}>
      {label && (
        <Label htmlFor={id}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      )}
      <Input
        ref={inputRef}
        value={value}
        onChange={onChange}
        className={inputClassName}
        id={id}
        placeholder={placeholder}
        required={required}
        {...inputProps}
      />
      {placesStatus === "unavailable" && apiKey && (
        <p className="mt-1 text-xs text-muted-foreground">
          Address suggestions are unavailable right now. You can still enter the address manually.
        </p>
      )}
    </div>
  );
};

export default AddressAutocomplete;
